import React, { useState, useEffect } from 'react';
import { Modal } from '../Common/Modal';
import { FiX, FiSave, FiUser, FiEdit } from 'react-icons/fi';
import DCFModal from './DCFModal';
import dcfService from '../../services/dcfService';
import reportService from '../../services/reportService';
import Spinner from '../Common/Spinner';
import Alert from '../Common/Alert';
import { formatDateDDMMMYYYY } from '../../utils/dateFormat';
import VisitModalHeader from './VisitModalHeader';
import './ReportReviewModal.css';

const ReportReviewModal = ({ isOpen, onClose, visit, resolvedPiName, reportId }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [dcfModalOpen, setDcfModalOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [dcfCounts, setDcfCounts] = useState({}); // Track DCF counts per question
  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen && reportId) {
      fetchReport();
      fetchDCFCounts();
    }
  }, [isOpen, reportId]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const data = await reportService.getById(reportId);
      setReport(data);
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  const fetchDCFCounts = async () => {
    if (!reportId) return;
    
    try {
      const dcfs = await dcfService.getByReport(reportId);
      const counts = {};
      dcfs.forEach(dcf => {
        const key = `${dcf.sectionName}-${dcf.questionNumber || 'general'}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      setDcfCounts(counts);
    } catch (err) {
      console.error('Error fetching DCF counts:', err);
    }
  };

  const handleOpenDCF = (sectionName, questionNumber, questionText) => {
    setSelectedSection(sectionName);
    setSelectedQuestion({ number: questionNumber, text: questionText });
    setDcfModalOpen(true);
  };

  const handleDCFClose = () => {
    setDcfModalOpen(false);
    fetchDCFCounts(); // Refresh counts after DCF operations
  };

  const handleSave = async () => {
    // TODO: Implement report save functionality
    setToast({ type: 'info', message: 'Report save functionality to be implemented' });
  };

  const getDCFCount = (sectionName, questionNumber) => {
    const key = `${sectionName}-${questionNumber || 'general'}`;
    return dcfCounts[key] || 0;
  };

  const hasOpenDCF = (sectionName, questionNumber) => {
    const key = `${sectionName}-${questionNumber || 'general'}`;
    return dcfCounts[key] > 0;
  };

  // Sample report sections - in real implementation, this would come from the report data
  const reportSections = [
    {
      name: "Investigator's Brochure",
      questions: [
        { number: "1", text: "Was Investigational Product Information (Investigator's Brochure OR Background information) discussed?" },
        { number: "2", text: "Did the Investigator and/or Staff raise any questions? If yes, specify questions and answers provided in Comments section." },
        { number: "3", text: "Were study end-points and criteria of evaluation / measurements discussed?" },
        { number: "4", text: "Were subject visit schedules, randomization and the subject discontinuation procedures discussed?" },
      ]
    },
    {
      name: "Protocol",
      questions: [
        { number: "1", text: "Was the protocol reviewed and discussed with the Investigator and site staff?" },
        { number: "2", text: "Were any protocol deviations identified?" },
        { number: "3", text: "Were inclusion and exclusion criteria reviewed?" },
      ]
    },
  ];

  const footer = (
    <>
      <button
        type="button"
        className="btn btn-secondary"
        onClick={onClose}
        disabled={submitting}
      >
        <FiX size={20} />
        Close
      </button>
      <button
        type="button"
        className="btn btn-primary"
        onClick={handleSave}
        disabled={submitting}
      >
        <FiSave size={20} />
        {submitting ? 'Saving...' : 'Save Report'}
      </button>
    </>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`Report: ${visit?.visitType || 'Visit'} Report`}
        size="xlarge"
        footer={footer}
        className="report-review-modal"
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            {visit && (
              <VisitModalHeader
                projectCode={visit.projectCode}
                siteName={visit.siteName}
                siteCode={visit.siteCode ?? visit.SiteCode}
                piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
              />
            )}
            {error && <Alert type="error" message={error} />}

            {/* Report Header */}
            <div className="report-header">
              <table className="report-header-table">
                <tbody>
                  <tr>
                    <td><strong>Project Code:</strong></td>
                    <td>
                      <input type="text" value={visit?.projectCode || '—'} disabled />
                    </td>
                    <td><strong>Site Name:</strong></td>
                    <td>
                      <input type="text" value={visit?.siteName || '—'} disabled />
                    </td>
                  </tr>
                  <tr>
                    <td><strong>PI Name:</strong></td>
                    <td>
                      <input type="text" value="Dr. Tapan Shah" disabled />
                    </td>
                    <td><strong>Visit Date:</strong></td>
                    <td>
                      <input type="text" value={visit?.plannedStartDate ? formatDateDDMMMYYYY(visit.plannedStartDate) : '—'} disabled />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Report Sections */}
            {reportSections.map((section, sectionIndex) => (
              <div key={sectionIndex} className="report-section">
                <h3 className="section-title">{section.name}</h3>
                <table className="report-questions-table">
                  <thead>
                    <tr>
                      <th style={{ width: '5%' }}></th>
                      <th style={{ width: '55%' }}>Question</th>
                      <th style={{ width: '15%' }}>Answer</th>
                      <th style={{ width: '25%' }}>Comments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.questions.map((question, qIndex) => (
                      <tr key={qIndex}>
                        <td>{question.number}</td>
                        <td>
                          <div className="question-cell">
                            {question.text}
                            <div className="question-actions">
                              <button
                                className="icon-btn"
                                title="Audit Trail"
                                onClick={() => {/* TODO: Open audit modal */}}
                              >
                                <FiUser size={16} />
                              </button>
                              <button
                                className={`icon-btn ${hasOpenDCF(section.name, question.number) ? 'has-dcf' : ''}`}
                                title="DCF Detail"
                                onClick={() => handleOpenDCF(section.name, question.number, question.text)}
                              >
                                <FiEdit size={16} />
                                {getDCFCount(section.name, question.number) > 0 && (
                                  <span className="dcf-badge">{getDCFCount(section.name, question.number)}</span>
                                )}
                              </button>
                            </div>
                          </div>
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Enter the answer"
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            className="form-input"
                            placeholder="Enter the Comments"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </>
        )}
      </Modal>

      {/* DCF Modal */}
      <DCFModal
        isOpen={dcfModalOpen}
        onClose={handleDCFClose}
        reportId={reportId}
        sectionName={selectedSection}
        questionNumber={selectedQuestion?.number}
        questionText={selectedQuestion?.text}
      />
      {toast && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
          <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
        </div>
      )}
    </>
  );
};

export default ReportReviewModal;
